import type {
  AutoWithdrawalDto,
  AutoWithdrawalNetworkCode,
  AutoWithdrawalRoutingMode,
  AutoWithdrawalRuleDto,
} from '@shared/api/contracts';
import {
  AUTO_WITHDRAWAL_PAIRS,
  type AutoWithdrawalDrafts,
  type AutoWithdrawalRuleDraft,
  getAutoWithdrawalPairKey,
} from '../model/auto-withdrawal';
import { findRuleByPairKey } from './normalize-auto-withdrawal';

const createEmptyDraft = (
  assetCode: AutoWithdrawalRuleDraft['assetCode'],
  networkCode: AutoWithdrawalRuleDraft['networkCode'],
  visualOrder: number,
): AutoWithdrawalRuleDraft => ({
  key: getAutoWithdrawalPairKey(assetCode, networkCode),
  assetCode,
  networkCode,
  destinationAddress: '',
  thresholdAmount: '',
  amountMode: 'FULL_BALANCE',
  reserveAmount: '',
  enabled: false,
  hasVerification: null,
  persistedAddress: null,
  visualOrder,
});

const mapRuleToDraft = (
  rule: AutoWithdrawalRuleDto,
  fallbackOrder: number,
): AutoWithdrawalRuleDraft => ({
  key: getAutoWithdrawalPairKey(rule.assetCode, rule.networkCode),
  assetCode: rule.assetCode,
  networkCode: rule.networkCode,
  destinationAddress: rule.destinationAddress ?? '',
  thresholdAmount: String(rule.thresholdAmount ?? ''),
  amountMode: rule.amountMode,
  reserveAmount: rule.reserveAmount != null ? String(rule.reserveAmount) : '',
  enabled: rule.enabled,
  hasVerification: rule.hasVerification,
  persistedAddress: rule.destinationAddress ?? null,
  visualOrder: rule.sortOrder ?? fallbackOrder,
});

export const createEmptySingleDraft = (): AutoWithdrawalRuleDraft => ({
  ...createEmptyDraft('USDT', 'TRC20', 0),
  enabled: true,
});

export const createEmptyDirectDrafts = (): AutoWithdrawalRuleDraft[] =>
  AUTO_WITHDRAWAL_PAIRS.map((pair, index) => createEmptyDraft(pair.assetCode, pair.networkCode, index));

export const mapSettingsToDrafts = (settings: AutoWithdrawalDto | null): AutoWithdrawalDrafts => {
  const routingMode: AutoWithdrawalRoutingMode = settings?.routingMode ?? 'SINGLE_TARGET_SWAP';
  const rules = settings?.rules ?? [];

  const directDrafts = createEmptyDirectDrafts().map((draft, index) => {
    const existing = findRuleByPairKey(rules, draft.key);
    return existing ? mapRuleToDraft(existing, index) : draft;
  });

  const singleRule =
    rules.find((rule) => rule.assetCode === 'USDT' && rule.enabled) ??
    rules.find((rule) => rule.assetCode === 'USDT') ??
    null;

  const singleDraft = singleRule
    ? { ...mapRuleToDraft(singleRule, 0), enabled: true }
    : createEmptySingleDraft();

  return { routingMode, singleDraft, directDrafts };
};

export const getEnabledDirectDrafts = (drafts: AutoWithdrawalRuleDraft[]): AutoWithdrawalRuleDraft[] =>
  drafts.filter((draft) => draft.enabled).sort((left, right) => left.visualOrder - right.visualOrder);

const renormalizeEnabledVisualOrders = (drafts: AutoWithdrawalRuleDraft[]): AutoWithdrawalRuleDraft[] => {
  const orderByKey = new Map(getEnabledDirectDrafts(drafts).map((draft, index) => [draft.key, index]));

  return drafts.map((draft) => {
    if (!draft.enabled) return draft;
    return { ...draft, visualOrder: orderByKey.get(draft.key) ?? draft.visualOrder };
  });
};

export const setDirectRuleEnabled = (
  drafts: AutoWithdrawalRuleDraft[],
  pairKey: string,
  enabled: boolean,
): AutoWithdrawalRuleDraft[] => {
  if (!enabled) {
    return renormalizeEnabledVisualOrders(
      drafts.map((draft) => (draft.key === pairKey ? { ...draft, enabled: false } : draft)),
    );
  }

  const nextOrder = getEnabledDirectDrafts(drafts).length;
  return drafts.map((draft) =>
    draft.key === pairKey ? { ...draft, enabled: true, visualOrder: nextOrder } : draft,
  );
};

export const reorderEnabledDirectDrafts = (
  drafts: AutoWithdrawalRuleDraft[],
  pairKey: string,
  direction: 'up' | 'down',
): AutoWithdrawalRuleDraft[] => {
  const enabled = getEnabledDirectDrafts(drafts);
  const index = enabled.findIndex((draft) => draft.key === pairKey);
  if (index === -1) return drafts;

  const swapIndex = direction === 'up' ? index - 1 : index + 1;
  if (swapIndex < 0 || swapIndex >= enabled.length) return drafts;

  const reordered = [...enabled];
  [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];

  const orderByKey = new Map(reordered.map((draft, order) => [draft.key, order]));

  return drafts.map((draft) => {
    if (!draft.enabled) return draft;
    const nextOrder = orderByKey.get(draft.key);
    return nextOrder == null ? draft : { ...draft, visualOrder: nextOrder };
  });
};

export const prefillDirectFromSingle = (
  singleDraft: AutoWithdrawalRuleDraft,
  directDrafts: AutoWithdrawalRuleDraft[],
): AutoWithdrawalRuleDraft[] => {
  const pairKey = getAutoWithdrawalPairKey('USDT', singleDraft.networkCode);
  return directDrafts.map((draft) => {
    if (draft.key !== pairKey) return draft;
    return {
      ...draft,
      ...singleDraft,
      key: draft.key,
      assetCode: draft.assetCode,
      networkCode: draft.networkCode,
      enabled: true,
      hasVerification: draft.hasVerification,
      persistedAddress: draft.persistedAddress,
      visualOrder: draft.visualOrder,
    };
  });
};

export const prefillSingleFromDirect = (
  directDrafts: AutoWithdrawalRuleDraft[],
  singleDraft: AutoWithdrawalRuleDraft,
): AutoWithdrawalRuleDraft => {
  const enabledUsdt = getEnabledDirectDrafts(directDrafts).filter((draft) => draft.assetCode === 'USDT');

  if (enabledUsdt.length === 0) {
    return { ...createEmptySingleDraft(), networkCode: singleDraft.networkCode || 'TRC20' };
  }

  const source = enabledUsdt[0];
  return {
    ...singleDraft,
    networkCode: source.networkCode,
    destinationAddress: source.destinationAddress,
    thresholdAmount: source.thresholdAmount,
    amountMode: source.amountMode,
    reserveAmount: source.reserveAmount,
    enabled: true,
    hasVerification: source.hasVerification,
    persistedAddress: source.persistedAddress,
  };
};

export const updateSingleNetwork = (
  singleDraft: AutoWithdrawalRuleDraft,
  networkCode: AutoWithdrawalNetworkCode,
): AutoWithdrawalRuleDraft => ({
  ...singleDraft,
  networkCode,
  key: getAutoWithdrawalPairKey('USDT', networkCode),
});
